import { WinSignageWebSocketClient } from './services/WebSocketClient';
import { ConnectionStatus, LogEntry, ClientStats, LogType } from './types';
import { environment } from './environments';
import './styles/main.css';
import logoSrc from './assets/logos/winsignage_logo_dark.svg';
import settingsIconSrc from './assets/icons/icon_settings.png';

// Set icon as CSS variable for use in styles
document.documentElement.style.setProperty('--settings-icon-url', `url(${settingsIconSrc})`);

class App {
  private client: WinSignageWebSocketClient;
  private connectionTimeInterval: number | null = null;
  private settingsButtonTimer: number | null = null;
  private playbackControlsTimer: number | null = null;
  private fullscreenButtonTimer: number | null = null;
  private currentAssetIndex: number = 0;
  private currentPlaylist: any[] = [];
  private playbackTimer: number | null = null;
  private consecutiveErrors: number = 0;
  private maxConsecutiveErrors: number = 5;
  private preloadedAssets: Map<string, string> = new Map();
  private wakeLock: WakeLockSentinel | null = null;
  private assetPlaybackCounts: Map<string, number> = new Map();
  private readonly PLAYBACK_COUNTS_KEY = 'winsignage-playback-counts';
  private assetStartTime: number = 0; // Track when current asset started playing
  private isLoadingPlaylist: boolean = false; // Prevent playback during playlist loading

  // --- Prefetch state ------------------------------------------------------

  /** In-flight downloads, keyed by asset key, so an asset is never fetched twice. */
  private prefetchInFlight: Map<string, Promise<void>> = new Map();
  /** Assets whose checksum did not match — skipped during playback rather than played corrupt. */
  private rejectedAssets: Set<string> = new Set();
  /** Assets whose download failed; they fall back to streaming straight from the hub. */
  private failedAssets: Set<string> = new Set();
  /** Approximate size of everything held in preloadedAssets, to honour maxCacheBytes. */
  private cachedBytes: number = 0;
  /**
   * Incremented on every new playlist. Background prefetches capture it and drop their
   * result if it changed, so a slow download from the previous playlist can never write
   * into the current cache.
   */
  private playlistGeneration: number = 0;
  /** Watchdog for media that loads but never starts (stalled network, codec refusal). */
  private mediaStartWatchdog: number | null = null;
  private checksumUnavailableWarned: boolean = false;

  // DOM Elements
  private clientIdInput!: HTMLInputElement;
  private clientSecretInput!: HTMLInputElement;
  private connectBtn!: HTMLButtonElement;
  private disconnectBtn!: HTMLButtonElement;
  private statusDiv!: HTMLDivElement;
  private logContainer!: HTMLDivElement;
  private clearLogBtn!: HTMLButtonElement;
  private exportLogBtn!: HTMLButtonElement;
  private settingsToggleBtn!: HTMLButtonElement;
  private settingsModal!: HTMLDivElement;
  private settingsCloseBtn!: HTMLButtonElement;
  private credentialPresetRadios!: NodeListOf<HTMLInputElement>;
  private spinnerContainer!: HTMLDivElement;
  private spinnerProgressText!: HTMLDivElement;
  private mediaPlayer!: HTMLDivElement;
  private videoPlayer!: HTMLVideoElement;
  private audioPlayer!: HTMLAudioElement;
  private imagePlayer!: HTMLImageElement;
  private logoContainer!: HTMLDivElement;
  private statusControls!: HTMLDivElement;
  private playbackControls!: HTMLDivElement;
  private playbackInfo!: HTMLDivElement;
  private prevBtn!: HTMLButtonElement;
  private nextBtn!: HTMLButtonElement;
  private fullscreenToggleBtn!: HTMLButtonElement;
  private playbackStatsContent!: HTMLDivElement;
  private showPlaybackStatsCheckbox!: HTMLInputElement;
  private playbackStatsOverlayElement!: HTMLDivElement;
  private muteVideosCheckbox!: HTMLInputElement;

  // Stats elements
  private connectionTimeEl!: HTMLDivElement;
  private messagesSentEl!: HTMLDivElement;
  private messagesReceivedEl!: HTMLDivElement;
  private lastPingEl!: HTMLDivElement;

  constructor() {
    // Log environment info
    console.log('🌍 Environment:', environment.production ? 'PRODUCTION' : 'DEVELOPMENT');
    console.log('🔗 Server URL:', environment.serverUrl);
    console.log('🐛 Debug Mode:', environment.enableDebugMode);

    this.client = new WinSignageWebSocketClient();
    this.initializeDOMElements();
    this.setupLogo();
    this.setupAccordions();
    this.setupEventListeners();
    this.setupClientCallbacks();
    this.loadSettings();
    this.loadPlaybackCounts();
    this.hideSpinner(); // Hide spinner on init
    this.setupSettingsButtonAutoHide();
    this.setupPlaybackControlsAutoHide();
    this.setupFullscreenButtonAutoHide();
    this.setupFullscreenWakeLock();
    // Auto-connect disabled - user must manually connect
  }

  private setupAccordions(): void {
    const accordionHeaders = document.querySelectorAll('.accordion-header');
    accordionHeaders.forEach((header) => {
      header.addEventListener('click', () => {
        const content = header.nextElementSibling as HTMLElement;
        const isOpen = content.classList.contains('open');

        if (isOpen) {
          content.classList.remove('open');
          header.classList.remove('active');
        } else {
          content.classList.add('open');
          header.classList.add('active');
        }
      });
    });

    // Open connection settings by default
    const connectionHeader = document.getElementById('connectionAccordion');
    if (connectionHeader) {
      connectionHeader.classList.add('active');
    }
  }

  private setupLogo(): void {
    const logoImg = document.querySelector('.logo-container img') as HTMLImageElement;
    if (logoImg) {
      logoImg.src = logoSrc;
    }

    const settingsIcon = document.querySelector('.settings-toggle-btn img') as HTMLImageElement;
    if (settingsIcon) {
      settingsIcon.src = settingsIconSrc;
    }
  }

  private initializeDOMElements(): void {
    this.clientIdInput = document.getElementById('clientId') as HTMLInputElement;
    this.clientSecretInput = document.getElementById('clientSecret') as HTMLInputElement;
    this.connectBtn = document.getElementById('connectBtn') as HTMLButtonElement;
    this.disconnectBtn = document.getElementById('disconnectBtn') as HTMLButtonElement;
    this.statusDiv = document.getElementById('status') as HTMLDivElement;
    this.logContainer = document.getElementById('logContainer') as HTMLDivElement;
    this.clearLogBtn = document.getElementById('clearLogBtn') as HTMLButtonElement;
    this.exportLogBtn = document.getElementById('exportLogBtn') as HTMLButtonElement;
    this.settingsToggleBtn = document.getElementById('settingsToggleBtn') as HTMLButtonElement;
    this.settingsModal = document.getElementById('settingsModal') as HTMLDivElement;
    this.settingsCloseBtn = document.getElementById('settingsCloseBtn') as HTMLButtonElement;
    this.credentialPresetRadios = document.querySelectorAll(
      'input[name="credentialPreset"]'
    ) as NodeListOf<HTMLInputElement>;
    this.spinnerContainer = document.querySelector('.spinner-container') as HTMLDivElement;
    this.spinnerProgressText = document.getElementById('spinnerProgressText') as HTMLDivElement;
    this.mediaPlayer = document.getElementById('mediaPlayer') as HTMLDivElement;
    this.videoPlayer = document.getElementById('videoPlayer') as HTMLVideoElement;
    this.audioPlayer = document.getElementById('audioPlayer') as HTMLAudioElement;
    this.imagePlayer = document.getElementById('imagePlayer') as HTMLImageElement;
    this.logoContainer = document.querySelector('.logo-container') as HTMLDivElement;
    this.statusControls = document.querySelector('.status-controls') as HTMLDivElement;
    this.playbackControls = document.getElementById('playbackControls') as HTMLDivElement;
    this.playbackInfo = document.getElementById('playbackInfo') as HTMLDivElement;
    this.prevBtn = document.getElementById('prevBtn') as HTMLButtonElement;
    this.nextBtn = document.getElementById('nextBtn') as HTMLButtonElement;
    this.fullscreenToggleBtn = document.getElementById('fullscreenToggleBtn') as HTMLButtonElement;
    this.playbackStatsContent = document.getElementById('playbackStatsContent') as HTMLDivElement;
    this.showPlaybackStatsCheckbox = document.getElementById(
      'showPlaybackStats'
    ) as HTMLInputElement;
    this.playbackStatsOverlayElement = document.getElementById(
      'playbackStatsOverlay'
    ) as HTMLDivElement;
    this.muteVideosCheckbox = document.getElementById('muteVideos') as HTMLInputElement;

    // Stats
    this.connectionTimeEl = document.getElementById('connectionTime') as HTMLDivElement;
    this.messagesSentEl = document.getElementById('messagesSent') as HTMLDivElement;
    this.messagesReceivedEl = document.getElementById('messagesReceived') as HTMLDivElement;
    this.lastPingEl = document.getElementById('lastPing') as HTMLDivElement;
  }

  private setupEventListeners(): void {
    this.connectBtn.addEventListener('click', () => this.handleConnect());
    this.disconnectBtn.addEventListener('click', () => this.handleDisconnect());
    this.clearLogBtn.addEventListener('click', () => this.handleClearLogs());
    this.exportLogBtn.addEventListener('click', () => this.handleExportLogs());
    this.settingsToggleBtn.addEventListener('click', () => this.handleToggleSettings());
    this.settingsCloseBtn.addEventListener('click', () => this.handleCloseSettings());

    // Close modal when clicking outside
    this.settingsModal.addEventListener('click', (e) => {
      if (e.target === this.settingsModal) {
        this.handleCloseSettings();
      }
    });

    // Save settings on change
    this.clientIdInput.addEventListener('change', () => this.saveSettings());
    this.clientSecretInput.addEventListener('change', () => this.saveSettings());

    // Radio button preset selection
    this.credentialPresetRadios.forEach((radio) => {
      radio.addEventListener('change', () => this.handlePresetChange(radio.value));
    });

    // Playback controls
    this.prevBtn.addEventListener('click', () => this.previousAsset());
    this.nextBtn.addEventListener('click', () => this.nextAsset());

    // Fullscreen toggle
    this.fullscreenToggleBtn.addEventListener('click', () => this.toggleFullscreen());

    // Playback stats toggle
    this.showPlaybackStatsCheckbox.addEventListener('change', () => this.togglePlaybackStats());

    // Mute videos toggle
    this.muteVideosCheckbox.addEventListener('change', () => this.handleMuteVideosChange());
  }

  private setupClientCallbacks(): void {
    this.client.setCallbacks({
      onStatusChange: (status) => this.updateStatus(status),
      onLogAdded: (log) => this.addLogToUI(log),
      onStatsUpdated: (stats) => this.updateStats(stats),
      onMessage: (message) => this.handleServerMessage(message),
      getPlaybackStats: () => this.getPlaybackStatsForServer(),
    });
  }

  private async handleConnect(): Promise<void> {
    const clientId = this.clientIdInput.value.trim();
    const clientSecret = this.clientSecretInput.value.trim();

    if (!clientId || !clientSecret) {
      alert('Please fill in all fields');
      return;
    }

    // Clear any existing playback before connecting
    this.stopPlayback();
    this.showDefaultView();

    try {
      await this.client.connect(environment.serverUrl, clientId, clientSecret);
    } catch (error) {
      console.error('Connection failed:', error);
    }
  }

  private handleDisconnect(): void {
    this.client.disconnect();
    // Stop playback and return to default view
    this.stopPlayback();
    this.showDefaultView();
  }

  private handleClearLogs(): void {
    this.client.clearLogs();
    this.logContainer.innerHTML = '';
  }

  private handleExportLogs(): void {
    const logs = this.client.exportLogs();
    const blob = new Blob([logs], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `winsignage-logs-${new Date().toISOString()}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  }

  private handleToggleSettings(): void {
    this.settingsModal.classList.add('show');
  }

  private handleCloseSettings(): void {
    this.settingsModal.classList.remove('show');
  }

  private showSpinner(): void {
    if (this.spinnerContainer) {
      this.spinnerContainer.style.display = 'block';
    }
  }

  private hideSpinner(): void {
    if (this.spinnerContainer) {
      this.spinnerContainer.style.display = 'none';
    }
  }

  private async handleServerMessage(message: any): Promise<void> {
    if (message.Type === 'playlist') {
      console.log('📋 Playlist command received:', message.Data);

      // Set loading flag to prevent any playback operations
      this.isLoadingPlaylist = true;

      // First, completely stop any existing playback and clear state
      this.stopPlayback();

      // Show logo and reset UI
      this.mediaPlayer.classList.remove('active');
      this.videoPlayer.classList.remove('active');
      this.imagePlayer.classList.remove('active');
      this.audioPlayer.classList.remove('active');
      this.logoContainer.style.display = 'block';
      this.showSpinner();

      // Check if message.Data exists and has playlists
      if (!message.Data) {
        console.warn('⚠️ No data in playlist message');
        this.hideSpinner();
        this.isLoadingPlaylist = false;
        return;
      }

      const playlists = message.Data.playlists || [];
      console.log('📋 Playlists array:', playlists);

      if (!Array.isArray(playlists) || playlists.length === 0) {
        // No playlists, keep showing default view
        console.log('ℹ️ No playlists found, showing default view');
        this.hideSpinner();
        this.isLoadingPlaylist = false;
      } else {
        // Collect all assets from all playlists
        const allAssets: any[] = [];
        playlists.forEach((playlist: any) => {
          if (playlist.assets && Array.isArray(playlist.assets)) {
            const assetsWithPlaylistId = playlist.assets.map((asset: any) => ({
              ...asset,
              playlistId: playlist.id,
            }));
            allAssets.push(...assetsWithPlaylistId);
          }
        });

        if (allAssets.length > 0) {
          // Clear old playback counts for assets not in new playlist
          this.cleanupOldPlaybackCounts(allAssets);

          // Invalidate any prefetch still running for the previous playlist before the
          // new assets are installed, so a late download cannot write into this cache.
          this.playlistGeneration++;

          this.currentPlaylist = allAssets;
          this.currentAssetIndex = 0; // Reset to first asset
          this.consecutiveErrors = 0; // Reset error counter

          console.log('🔄 Prefetching the first asset before playback...');
          console.log('📍 NEW PLAYLIST: Starting from asset index:', this.currentAssetIndex);
          await this.cacheAssets(); // Waits for the lookahead only; the rest continues in the background
          console.log('✅ Lead assets ready, starting playback...');
          console.log(
            '📍 NEW PLAYLIST: Current asset index before startPlayback:',
            this.currentAssetIndex
          );
          this.hideSpinner();

          // Clear loading flag just before starting playback
          this.isLoadingPlaylist = false;
          this.startPlayback();
          console.log(
            '📍 NEW PLAYLIST: Current asset index after startPlayback:',
            this.currentAssetIndex
          );
        } else {
          console.log('ℹ️ No assets found in playlists, showing default view');
          this.hideSpinner();
          this.isLoadingPlaylist = false;
        }
      }
    }
  }

  private showDefaultView(): void {
    console.log('🏠 showDefaultView called');
    // Don't call stopPlayback here to avoid clearing playlist during transitions
    this.mediaPlayer.classList.remove('active');
    this.videoPlayer.classList.remove('active');
    this.audioPlayer.classList.remove('active');
    this.imagePlayer.classList.remove('active');
    this.playbackControls.style.display = 'none'; // Hide playback controls
    this.logoContainer.style.display = 'block';
    // Status should always be visible, don't hide it
  }

  private hideDefaultView(): void {
    this.logoContainer.style.display = 'none';
    // Status should always be visible, don't hide it
  }

  private startPlayback(): void {
    console.log('🎬 startPlayback called, playlist length:', this.currentPlaylist.length);
    console.log('🎬 startPlayback called, currentAssetIndex:', this.currentAssetIndex);

    if (this.currentPlaylist.length === 0) {
      console.warn('⚠️ startPlayback: Playlist is empty, showing default view');
      this.showDefaultView();
      return;
    }

    this.consecutiveErrors = 0; // Reset error counter when starting playback
    this.hideDefaultView();
    this.mediaPlayer.classList.add('active');
    this.playbackControls.style.display = 'flex'; // Enable playback controls
    this.playbackControls.classList.add('hidden'); // Start hidden - will show on touch/click
    console.log('🎬 Calling playCurrentAsset...');
    this.playCurrentAsset();
  }

  private playCurrentAsset(): void {
    // Don't play anything if we're loading a new playlist
    if (this.isLoadingPlaylist) {
      console.log('⏸️ Playback blocked - loading new playlist');
      return;
    }

    // Any timer left over from a previous asset would advance the playlist a second time.
    this.clearPlaybackTimers();

    // Check if playlist is empty or invalid
    if (!this.currentPlaylist || this.currentPlaylist.length === 0) {
      console.warn('⚠️ Playlist is empty, cannot play asset');
      this.showDefaultView();
      return;
    }

    if (this.currentAssetIndex >= this.currentPlaylist.length) {
      this.currentAssetIndex = 0; // Loop back to start
    }

    // Assets whose checksum did not match are skipped rather than removed, so the index
    // the player is sitting on stays valid while background prefetching is still running.
    const playableIndex = this.findPlayableIndex(this.currentAssetIndex);

    if (playableIndex === -1) {
      this.log('⚠️ No playable assets left in the playlist', LogType.Warning);
      this.showDefaultView();
      return;
    }

    this.currentAssetIndex = playableIndex;

    const asset = this.currentPlaylist[this.currentAssetIndex];

    if (!asset) {
      console.warn('⚠️ Asset is undefined, skipping');
      setTimeout(() => this.nextAsset(), 100);
      return;
    }

    // Get the following asset downloading now, so it is already in memory by the time
    // this one finishes.
    this.prioritizeAsset((this.currentAssetIndex + 1) % this.currentPlaylist.length);

    console.log(
      `🎬 Playing asset ${this.currentAssetIndex + 1}/${this.currentPlaylist.length}:`,
      asset
    );
    console.log('🔍 Asset file_type:', asset.file_type);

    // Track when asset started
    this.assetStartTime = Date.now();

    // Track asset playback
    const assetId = asset.id || asset.assetId || asset.asset_id;
    if (assetId) {
      this.incrementAssetPlayback(assetId, asset.playlistId);
    }

    // Update playback info with asset name
    const assetName = asset.name || asset.file_name || asset.fileName || 'Unknown';
    this.playbackInfo.textContent = assetName;

    if (asset.file_type === 'video') {
      console.log('📹 Playing video...');
      this.playVideo(asset);
    } else if (asset.file_type === 'image') {
      console.log('🖼️ Playing image...');
      this.playImage(asset);
    } else if (asset.file_type === 'audio') {
      console.log('🎵 Playing audio...');
      this.playAudio(asset);
    } else {
      console.warn('⚠️ Unknown asset type, skipping:', asset.file_type);
      // Unknown type, skip to next with delay to prevent stack overflow
      setTimeout(() => this.nextAsset(), 100);
    }
  }

  /**
   * SHA-256 of a downloaded asset, or null when the platform cannot compute one.
   *
   * `crypto.subtle` only exists in secure contexts, so on a player served over plain
   * http (a very normal kiosk setup) it is undefined. This used to throw inside the
   * download loop, which aborted caching for *every* asset and reported it as a generic
   * "UNKNOWN Error" — the whole prefetch layer silently degraded to direct streaming.
   * Returning null instead lets the caller cache the asset and simply skip verification.
   */
  private async calculateSHA256(blob: Blob): Promise<string | null> {
    const subtle = globalThis.crypto?.subtle;

    if (!subtle) {
      if (!this.checksumUnavailableWarned) {
        this.checksumUnavailableWarned = true;
        this.log(
          '⚠️ crypto.subtle unavailable (insecure origin?) — assets will be cached without checksum verification',
          LogType.Warning
        );
      }
      return null;
    }

    try {
      const buffer = await blob.arrayBuffer();
      const hashBuffer = await subtle.digest('SHA-256', buffer);
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
    } catch (error) {
      this.log(`⚠️ Could not compute checksum: ${error}`, LogType.Warning);
      return null;
    }
  }

  /**
   * First index at or after `from` whose asset is not rejected, wrapping once around the
   * playlist. Returns -1 when every asset has been rejected.
   */
  private findPlayableIndex(from: number): number {
    const total = this.currentPlaylist.length;

    for (let offset = 0; offset < total; offset++) {
      const index = (from + offset) % total;
      const asset = this.currentPlaylist[index];

      if (asset && !this.rejectedAssets.has(this.getAssetKey(asset, index))) return index;
    }

    return -1;
  }

  private clearPlaybackTimers(): void {
    if (this.playbackTimer !== null) {
      clearTimeout(this.playbackTimer);
      this.playbackTimer = null;
    }

    if (this.mediaStartWatchdog !== null) {
      clearTimeout(this.mediaStartWatchdog);
      this.mediaStartWatchdog = null;
    }
  }

  /**
   * Guards against media that loads but never starts — a stalled download or a codec the
   * TV silently refuses fires neither `ended` nor `error`, which used to leave the player
   * frozen on one asset indefinitely.
   */
  private armMediaStartWatchdog(assetKey: string): void {
    if (this.mediaStartWatchdog !== null) clearTimeout(this.mediaStartWatchdog);

    this.mediaStartWatchdog = window.setTimeout(() => {
      this.mediaStartWatchdog = null;
      this.log(`⏱️ ${assetKey} did not start playing in time — skipping`, LogType.Warning);
      this.handlePlaybackFailure();
    }, environment.mediaStartTimeoutMs);
  }

  private disarmMediaStartWatchdog(): void {
    if (this.mediaStartWatchdog !== null) {
      clearTimeout(this.mediaStartWatchdog);
      this.mediaStartWatchdog = null;
    }
  }

  /** Shared error path: trips the circuit breaker or advances to the next asset. */
  private handlePlaybackFailure(): void {
    if (this.isLoadingPlaylist) return;

    // An image failing mid-display would otherwise leave its duration timer running
    // alongside the retry timer below, and the playlist would advance twice.
    this.clearPlaybackTimers();

    this.consecutiveErrors++;

    if (this.consecutiveErrors >= this.maxConsecutiveErrors) {
      this.log('❌ Too many consecutive errors, stopping playback', LogType.Error);
      this.clearPlaybackTimers();
      this.showDefaultView();
      this.consecutiveErrors = 0;
      return;
    }

    this.playbackTimer = window.setTimeout(() => {
      this.playbackTimer = null;
      this.nextAsset();
    }, 2000);
  }

  /** Stable identity for an asset. Never undefined — a keyless asset would poison the cache maps. */
  private getAssetKey(asset: any, index: number): string {
    return String(
      asset?.id || asset?.assetId || asset?.file_name || asset?.fileName || `index-${index}`
    );
  }

  private releaseCachedAssets(): void {
    this.preloadedAssets.forEach((blobUrl) => URL.revokeObjectURL(blobUrl));
    this.preloadedAssets.clear();
    this.prefetchInFlight.clear();
    this.rejectedAssets.clear();
    this.failedAssets.clear();
    this.cachedBytes = 0;
  }

  /**
   * Prefetches playlist assets into memory.
   *
   * Only the first `prefetchLookahead` assets are awaited; everything else keeps
   * downloading in the background while playback is already running. The previous
   * implementation downloaded the whole playlist sequentially before showing anything,
   * so a playlist with a few large videos parked the screen on the logo for minutes.
   */
  private async cacheAssets(): Promise<void> {
    const generation = this.playlistGeneration;
    const total = this.currentPlaylist.length;

    this.log(`🔄 Prefetching ${total} asset(s)...`, LogType.Info);

    const lookahead = Math.max(1, Math.min(environment.prefetchLookahead, total));

    // Await just enough to start playing without a black screen.
    for (let i = 0; i < lookahead; i++) {
      this.spinnerProgressText.textContent = `Loading assets... ${i + 1}/${total}`;
      await this.prefetchAsset(i, generation);

      if (generation !== this.playlistGeneration) return;
    }

    // The rest streams in behind playback.
    void this.prefetchRemaining(generation);

    this.spinnerProgressText.textContent = '';
  }

  /** Downloads the remainder of the playlist with bounded concurrency. */
  private async prefetchRemaining(generation: number): Promise<void> {
    const startIndex = Math.max(
      1,
      Math.min(environment.prefetchLookahead, this.currentPlaylist.length)
    );
    let nextIndex = startIndex;

    const worker = async (): Promise<void> => {
      while (generation === this.playlistGeneration) {
        const index = nextIndex++;

        if (index >= this.currentPlaylist.length) return;

        await this.prefetchAsset(index, generation);
      }
    };

    const workerCount = Math.max(1, environment.prefetchConcurrency);
    await Promise.all(Array.from({ length: workerCount }, () => worker()));

    if (generation !== this.playlistGeneration) return;

    this.log(
      `📦 Prefetch finished — ${this.preloadedAssets.size} cached, ${this.failedAssets.size} streaming, ${this.rejectedAssets.size} rejected`,
      LogType.Info
    );
  }

  /**
   * Moves an asset to the front of the queue. Called when playback advances, so the asset
   * about to be shown is fetched before the ones further down the playlist.
   */
  private prioritizeAsset(index: number): void {
    if (index < 0 || index >= this.currentPlaylist.length) return;

    const asset = this.currentPlaylist[index];
    if (!asset) return;

    // Give an asset that failed to download one more chance per playlist loop. Without
    // this, a single network blip at start-up would leave it streaming from the hub for
    // as long as the playlist runs — which on signage is days.
    this.failedAssets.delete(this.getAssetKey(asset, index));

    void this.prefetchAsset(index, this.playlistGeneration);
  }

  /**
   * Fetches one asset, verifies its checksum and caches it as a blob URL.
   * Idempotent and concurrency-safe: repeat calls join the in-flight promise instead of
   * starting a second download.
   */
  private prefetchAsset(index: number, generation: number): Promise<void> {
    const asset = this.currentPlaylist[index];

    if (!asset) return Promise.resolve();

    const key = this.getAssetKey(asset, index);

    // Already settled one way or another.
    if (this.preloadedAssets.has(key) || this.rejectedAssets.has(key) || this.failedAssets.has(key)) {
      return Promise.resolve();
    }

    const inFlight = this.prefetchInFlight.get(key);
    if (inFlight) return inFlight;

    const promise = this.downloadAndCacheAsset(asset, key, generation).finally(() => {
      this.prefetchInFlight.delete(key);
    });

    this.prefetchInFlight.set(key, promise);

    return promise;
  }

  private async downloadAndCacheAsset(asset: any, key: string, generation: number): Promise<void> {
    const url = this.getAssetDownloadUrl(asset);
    const controller = new AbortController();
    const timeoutId = window.setTimeout(
      () => controller.abort(),
      environment.assetFetchTimeoutMs
    );

    try {
      // A single CORS request. A `no-cors` retry used to sit here as a fallback, but an
      // opaque response reports ok === false and an empty body, so that path could never
      // produce a usable asset — it only doubled the traffic and masked the real error.
      const response = await fetch(url, {
        signal: controller.signal,
        mode: 'cors',
        credentials: 'include',
        cache: 'default',
      });

      if (!response.ok) {
        this.failedAssets.add(key);
        this.log(
          `⚠️ Download failed for ${key}: HTTP ${response.status} — will stream directly`,
          LogType.Warning
        );
        return;
      }

      const blob = await response.blob();

      if (blob.size === 0) {
        this.failedAssets.add(key);
        this.log(`⚠️ Empty response body for ${key} — will stream directly`, LogType.Warning);
        return;
      }

      if (!(await this.verifyChecksum(asset, blob, key))) return;

      // A new playlist arrived while this was downloading: the blob belongs to a playlist
      // nobody is playing any more.
      if (generation !== this.playlistGeneration) return;

      if (this.cachedBytes + blob.size > environment.maxCacheBytes) {
        this.failedAssets.add(key);
        this.log(
          `💾 Cache budget of ${Math.round(environment.maxCacheBytes / 1024 / 1024)} MB reached — ${key} will stream directly`,
          LogType.Warning
        );
        return;
      }

      this.preloadedAssets.set(key, URL.createObjectURL(blob));
      this.cachedBytes += blob.size;

      this.log(`✅ Cached ${key} (${Math.round(blob.size / 1024)} KB)`, LogType.Success);
    } catch (error) {
      const name = error instanceof Error ? error.name : 'Unknown';
      const message = error instanceof Error ? error.message : String(error);
      const kind = name === 'AbortError' ? 'TIMEOUT' : 'NETWORK';

      this.failedAssets.add(key);
      this.log(
        `❌ ${kind} while fetching ${key} (${message}) — will stream directly`,
        LogType.Error
      );
    } finally {
      // This used to run inside the try, so a throwing fetch left the abort timer pending.
      window.clearTimeout(timeoutId);
    }
  }

  /**
   * True when the asset may be cached. A mismatch is recorded so playback skips the asset
   * instead of showing corrupt media.
   *
   * Note this does NOT remove the asset from currentPlaylist: prefetching runs while
   * playback is in progress, and splicing the array would shift the index out from under
   * the player.
   */
  private async verifyChecksum(asset: any, blob: Blob, key: string): Promise<boolean> {
    const expected = String(asset?.file_checksum ?? asset?.fileChecksum ?? '')
      .trim()
      .toLowerCase();

    // The hub does not always know a checksum (older records); nothing to verify against.
    if (!expected) return true;

    const actual = await this.calculateSHA256(blob);

    // Platform cannot hash — caching an unverified asset beats showing nothing.
    if (actual === null) return true;

    if (actual.toLowerCase() !== expected) {
      this.rejectedAssets.add(key);
      this.log(
        `❌ Checksum mismatch for ${key} (expected ${expected}, got ${actual}) — asset skipped`,
        LogType.Error
      );
      return false;
    }

    return true;
  }

  private getAssetDownloadUrl(asset: any): string {
    // Use assetDownloadBaseUrl from environment
    const baseUrl = environment.assetDownloadBaseUrl;
    const downloadUrl = new URL(`${baseUrl}/websocket/asset/download`);

    downloadUrl.searchParams.append('clientId', this.clientIdInput.value);
    downloadUrl.searchParams.append('assetId', asset.id || asset.assetId || '');
    downloadUrl.searchParams.append(
      'isSystemAsset',
      asset.is_system_asset || asset.isSystemAsset || 'false'
    );

    const fileName = asset.file_name || asset.fileName;
    if (fileName) {
      downloadUrl.searchParams.append('fileName', fileName);
    }

    return downloadUrl.toString();
  }

  private playVideo(asset: any): void {
    this.imagePlayer.classList.remove('active');
    this.audioPlayer.classList.remove('active');
    this.audioPlayer.pause();
    this.videoPlayer.classList.add('active');

    // Use cached asset if available, otherwise stream straight from the hub
    const assetKey = this.getAssetKey(asset, this.currentAssetIndex);
    const cachedUrl = this.preloadedAssets.get(assetKey);
    const videoUrl = cachedUrl || this.getAssetDownloadUrl(asset);

    this.armMediaStartWatchdog(assetKey);

    this.log(`🎥 Playing video - Asset Key: ${assetKey}`, LogType.Info);
    this.log(
      `🎥 Using ${cachedUrl ? 'CACHED' : 'DIRECT'} URL`,
      cachedUrl ? LogType.Success : LogType.Warning
    );
    this.log(`🎥 Video URL: ${videoUrl}`, LogType.Info);

    // Remove all event listeners by cloning the element. The clone inherits the previous
    // asset's src attribute, so it starts fetching the old media and is aborted a moment
    // later when the new src is assigned — clearing it first avoids that wasted load.
    const newVideoPlayer = this.videoPlayer.cloneNode(true) as HTMLVideoElement;
    newVideoPlayer.removeAttribute('src');
    this.videoPlayer.parentNode?.replaceChild(newVideoPlayer, this.videoPlayer);
    this.videoPlayer = newVideoPlayer;
    this.videoPlayer.classList.add('active');

    this.videoPlayer.src = videoUrl;
    this.videoPlayer.muted = this.muteVideosCheckbox.checked;

    // Track when video actually starts playing (not just loaded)
    let hasStartedPlaying = false;

    this.videoPlayer.onloadstart = () => {
      const networkState = this.videoPlayer.networkState;
      const readyState = this.videoPlayer.readyState;
      const networkStates = [
        'NETWORK_EMPTY',
        'NETWORK_IDLE',
        'NETWORK_LOADING',
        'NETWORK_NO_SOURCE',
      ];
      const readyStates = [
        'HAVE_NOTHING',
        'HAVE_METADATA',
        'HAVE_CURRENT_DATA',
        'HAVE_FUTURE_DATA',
        'HAVE_ENOUGH_DATA',
      ];
      this.log(
        `🎥 Video loadstart for ${assetKey} - Network: ${networkStates[networkState]}, Ready: ${readyStates[readyState]}`,
        LogType.Info
      );
    };

    this.videoPlayer.onloadedmetadata = () => {
      const networkState = this.videoPlayer.networkState;
      const readyState = this.videoPlayer.readyState;
      const networkStates = [
        'NETWORK_EMPTY',
        'NETWORK_IDLE',
        'NETWORK_LOADING',
        'NETWORK_NO_SOURCE',
      ];
      const readyStates = [
        'HAVE_NOTHING',
        'HAVE_METADATA',
        'HAVE_CURRENT_DATA',
        'HAVE_FUTURE_DATA',
        'HAVE_ENOUGH_DATA',
      ];
      this.log(
        `🎥 Video metadata loaded for ${assetKey} - Network: ${networkStates[networkState]}, Ready: ${readyStates[readyState]}`,
        LogType.Info
      );
    };

    this.videoPlayer.onloadeddata = () => {
      this.consecutiveErrors = 0;
      const networkState = this.videoPlayer.networkState;
      const readyState = this.videoPlayer.readyState;
      const networkStates = [
        'NETWORK_EMPTY',
        'NETWORK_IDLE',
        'NETWORK_LOADING',
        'NETWORK_NO_SOURCE',
      ];
      const readyStates = [
        'HAVE_NOTHING',
        'HAVE_METADATA',
        'HAVE_CURRENT_DATA',
        'HAVE_FUTURE_DATA',
        'HAVE_ENOUGH_DATA',
      ];
      this.log(
        `🎥 Video data loaded for ${assetKey} - Network: ${networkStates[networkState]}, Ready: ${readyStates[readyState]}`,
        LogType.Success
      );
    };

    this.videoPlayer.onplaying = () => {
      hasStartedPlaying = true;
      this.disarmMediaStartWatchdog();
      this.log(`▶️ Video started playing: ${assetKey}`, LogType.Success);
    };

    this.videoPlayer.onended = () => {
      // Don't navigate if we're loading a new playlist
      if (this.isLoadingPlaylist) {
        console.log('⏸️ Video ended but playlist is loading, ignoring');
        return;
      }
      // Only go to next if video actually played
      if (hasStartedPlaying) {
        console.log('⏹️ Video ended naturally');
        this.nextAsset();
      }
    };

    this.videoPlayer.onerror = () => {
      // Don't navigate if we're loading a new playlist
      if (this.isLoadingPlaylist) {
        this.log('⏸️ Video error but playlist is loading, ignoring', LogType.Warning);
        return;
      }

      const error = this.videoPlayer.error;
      const errorCode = error?.code || 'unknown';
      const errorMessage = error?.message || 'No error message';

      this.log(
        `❌ Video playback error: ${assetKey}, Code: ${errorCode}, Msg: ${errorMessage}`,
        LogType.Error
      );

      this.disarmMediaStartWatchdog();
      this.handlePlaybackFailure();
    };

    this.videoPlayer.play().catch((error) => {
      console.error('Failed to play video:', error);
      this.disarmMediaStartWatchdog();
      this.handlePlaybackFailure();
    });
  }

  private playImage(asset: any): void {
    this.clearPlaybackTimers();

    this.videoPlayer.classList.remove('active');
    this.videoPlayer.pause();
    this.audioPlayer.classList.remove('active');
    this.audioPlayer.pause();
    this.imagePlayer.classList.add('active');

    // Use cached asset if available, otherwise stream straight from the hub
    const assetKey = this.getAssetKey(asset, this.currentAssetIndex);
    const cachedUrl = this.preloadedAssets.get(assetKey);
    const imageUrl = cachedUrl || this.getAssetDownloadUrl(asset);

    // Remove all event listeners by cloning; drop the inherited src so the clone does not
    // re-fetch the previous asset before the new one is assigned.
    const newImagePlayer = this.imagePlayer.cloneNode(true) as HTMLImageElement;
    newImagePlayer.removeAttribute('src');
    this.imagePlayer.parentNode?.replaceChild(newImagePlayer, this.imagePlayer);
    this.imagePlayer = newImagePlayer;
    this.imagePlayer.classList.add('active');

    this.imagePlayer.src = imageUrl;

    this.imagePlayer.onload = () => {
      this.consecutiveErrors = 0;
    };

    this.imagePlayer.onerror = () => {
      console.error('Image load error:', asset);
      this.handlePlaybackFailure();
    };

    const duration = (asset.duration || 5) * 1000;
    const minDuration = 1000; // Minimum 1 second for images
    const actualDuration = Math.max(duration, minDuration);

    this.playbackTimer = window.setTimeout(() => {
      const playDuration = Date.now() - this.assetStartTime;
      console.log(`🖼️ Image displayed for ${playDuration}ms`);
      this.nextAsset();
    }, actualDuration);
  }

  private playAudio(asset: any): void {
    this.imagePlayer.classList.remove('active');
    this.videoPlayer.classList.remove('active');
    this.videoPlayer.pause();
    this.audioPlayer.classList.add('active');

    // Show logo and status while playing audio
    this.logoContainer.style.display = 'block';
    this.statusControls.style.display = 'flex';

    const assetKey = this.getAssetKey(asset, this.currentAssetIndex);
    const cachedUrl = this.preloadedAssets.get(assetKey);
    const audioUrl = cachedUrl || this.getAssetDownloadUrl(asset);

    this.armMediaStartWatchdog(assetKey);

    // Remove all event listeners by cloning; drop the inherited src so the clone does not
    // re-fetch the previous asset before the new one is assigned.
    const newAudioPlayer = this.audioPlayer.cloneNode(true) as HTMLAudioElement;
    newAudioPlayer.removeAttribute('src');
    this.audioPlayer.parentNode?.replaceChild(newAudioPlayer, this.audioPlayer);
    this.audioPlayer = newAudioPlayer;
    this.audioPlayer.classList.add('active');

    console.log('🎵 Loading audio from:', audioUrl);
    this.audioPlayer.src = audioUrl;
    this.audioPlayer.load();

    this.audioPlayer.onloadeddata = () => {
      this.consecutiveErrors = 0;
      console.log('🎵 Audio loaded successfully');
    };

    this.audioPlayer.onplaying = () => {
      this.disarmMediaStartWatchdog();
    };

    this.audioPlayer.onended = () => {
      // Don't navigate if we're loading a new playlist
      if (this.isLoadingPlaylist) {
        console.log('⏸️ Audio ended but playlist is loading, ignoring');
        return;
      }
      console.log('🎵 Audio playback ended');
      this.nextAsset();
    };

    this.audioPlayer.onerror = (e) => {
      // Don't navigate if we're loading a new playlist
      if (this.isLoadingPlaylist) {
        console.log('⏸️ Audio error but playlist is loading, ignoring');
        return;
      }

      console.error('Audio playback error:', asset);
      console.error('Audio error event:', e);
      console.error('Audio error code:', this.audioPlayer.error?.code);
      console.error('Audio error message:', this.audioPlayer.error?.message);

      this.disarmMediaStartWatchdog();
      this.handlePlaybackFailure();
    };

    this.audioPlayer.play().catch((error) => {
      console.error('Failed to play audio:', error);
      console.error('Error name:', error.name);
      console.error('Error message:', error.message);

      this.disarmMediaStartWatchdog();
      this.handlePlaybackFailure();
    });
  }

  private nextAsset(): void {
    // Don't navigate if we're loading a new playlist
    if (this.isLoadingPlaylist) {
      console.log('⏸️ Next asset blocked - loading new playlist');
      return;
    }

    // Stop current media before switching
    this.videoPlayer.pause();
    this.audioPlayer.pause();

    // Clear event handlers to prevent old callbacks
    this.videoPlayer.onended = null;
    this.videoPlayer.onerror = null;
    this.audioPlayer.onended = null;
    this.audioPlayer.onerror = null;

    this.clearPlaybackTimers();

    this.currentAssetIndex++;
    this.playCurrentAsset();
  }

  private previousAsset(): void {
    // Don't navigate if we're loading a new playlist
    if (this.isLoadingPlaylist) {
      console.log('⏸️ Previous asset blocked - loading new playlist');
      return;
    }

    // Stop current media before switching
    this.videoPlayer.pause();
    this.audioPlayer.pause();

    // Clear event handlers to prevent old callbacks
    this.videoPlayer.onended = null;
    this.videoPlayer.onerror = null;
    this.audioPlayer.onended = null;
    this.audioPlayer.onerror = null;

    this.clearPlaybackTimers();

    this.currentAssetIndex--;
    if (this.currentAssetIndex < 0) {
      this.currentAssetIndex = this.currentPlaylist.length - 1; // Loop to end
    }
    this.playCurrentAsset();
  }

  private stopPlayback(): void {
    console.log('🛑 Stopping playback...');

    // Any background prefetch still running belongs to the playlist being torn down.
    this.playlistGeneration++;

    this.clearPlaybackTimers();

    // Stop and clear all media elements - pause first to stop any playback
    this.videoPlayer.pause();
    this.audioPlayer.pause();

    // Clear all event handlers to prevent any callbacks
    this.videoPlayer.onloadeddata = null;
    this.videoPlayer.onplaying = null;
    this.videoPlayer.onended = null;
    this.videoPlayer.onerror = null;

    this.audioPlayer.onloadeddata = null;
    this.audioPlayer.onplaying = null;
    this.audioPlayer.onended = null;
    this.audioPlayer.onerror = null;

    this.imagePlayer.onload = null;
    this.imagePlayer.onerror = null;

    // Clear sources
    this.videoPlayer.src = '';
    this.videoPlayer.load(); // Force reload to clear buffer

    this.audioPlayer.src = '';
    this.audioPlayer.load();

    this.imagePlayer.src = '';

    // Remove active classes
    this.videoPlayer.classList.remove('active');
    this.audioPlayer.classList.remove('active');
    this.imagePlayer.classList.remove('active');

    // Hide media player
    this.mediaPlayer.classList.remove('active');

    // Hide playback controls
    this.playbackControls.style.display = 'none';

    // Show logo when stopping playback
    this.logoContainer.style.display = 'block';
    // Status should always be visible

    // Revoke every blob URL and reset the prefetch bookkeeping in one place, so a cache
    // reset can never leave object URLs behind holding on to their buffers.
    this.releaseCachedAssets();

    this.currentPlaylist = [];
    this.currentAssetIndex = 0;
    this.assetStartTime = 0; // Reset asset start time

    console.log(
      '✅ Playback stopped, all event handlers cleared, index reset to:',
      this.currentAssetIndex
    );
  }

  private handlePresetChange(preset: string): void {
    const presets: Record<string, { clientId: string; clientSecret: string }> = {
      demo1: { clientId: 'CBXMWZJ4', clientSecret: '045XNQL3' },
      demo2: { clientId: 'LPSSKA03', clientSecret: 'RBHG5N98' },
      demo3: { clientId: '3ZHOGJSD', clientSecret: 'DRKNPY1F' },
      custom: { clientId: 'U7ZVJUJN', clientSecret: 'NVXVE5VB' },
    };

    const selectedPreset = presets[preset];
    if (selectedPreset) {
      this.clientIdInput.value = selectedPreset.clientId;
      this.clientSecretInput.value = selectedPreset.clientSecret;

      // Enable/disable inputs based on preset
      if (preset === 'custom') {
        this.clientIdInput.disabled = false;
        this.clientSecretInput.disabled = false;
      } else {
        this.clientIdInput.disabled = true;
        this.clientSecretInput.disabled = true;
      }

      this.saveSettings();
    }
  }

  private updateStatus(status: ConnectionStatus): void {
    this.statusDiv.className = `status ${status}`;

    switch (status) {
      case ConnectionStatus.Disconnected:
        this.statusDiv.textContent = 'Disconnected';
        this.connectBtn.disabled = false;
        this.disconnectBtn.disabled = true;
        this.enableCredentialInputs();
        if (this.connectionTimeInterval) {
          clearInterval(this.connectionTimeInterval);
          this.connectionTimeInterval = null;
        }
        break;

      case ConnectionStatus.Connecting:
        this.statusDiv.textContent = 'Connecting...';
        this.connectBtn.disabled = true;
        this.disconnectBtn.disabled = false;
        this.disableCredentialInputs();
        break;

      case ConnectionStatus.Authenticating:
        this.statusDiv.textContent = 'Authenticating...';
        this.connectBtn.disabled = true;
        this.disconnectBtn.disabled = true;
        this.disableCredentialInputs();
        break;

      case ConnectionStatus.Connected:
        this.statusDiv.textContent = 'Connected';
        this.connectBtn.disabled = true;
        this.disconnectBtn.disabled = false;
        this.disableCredentialInputs();
        this.startConnectionTimer();
        break;
    }
  }

  private disableCredentialInputs(): void {
    this.clientIdInput.disabled = true;
    this.clientSecretInput.disabled = true;
    this.credentialPresetRadios.forEach((radio) => {
      radio.disabled = true;
    });
  }

  private enableCredentialInputs(): void {
    this.clientIdInput.disabled = false;
    this.clientSecretInput.disabled = false;
    this.credentialPresetRadios.forEach((radio) => {
      radio.disabled = false;
    });
  }

  private addLogToUI(log: LogEntry): void {
    const entry = document.createElement('div');
    entry.className = 'log-entry';

    const time = document.createElement('span');
    time.className = 'log-time';
    time.textContent = log.timestamp.toLocaleTimeString('en-US');

    const type = document.createElement('span');
    type.className = `log-type log-type-${log.type}`;
    type.textContent = `[${log.type.toUpperCase()}]`;

    const message = document.createElement('span');
    message.className = 'log-message';
    message.textContent = log.message;

    entry.appendChild(time);
    entry.appendChild(type);
    entry.appendChild(message);

    this.logContainer.appendChild(entry);
    this.logContainer.scrollTop = this.logContainer.scrollHeight;
  }

  private log(message: string, logType: LogType = LogType.Info): void {
    // Log to console
    const consoleMsg = `[${logType.toUpperCase()}] ${message}`;
    switch (logType) {
      case LogType.Error:
        console.error(consoleMsg);
        break;
      case LogType.Warning:
        console.warn(consoleMsg);
        break;
      default:
        console.log(consoleMsg);
    }

    // Log to UI
    this.addLogToUI({
      timestamp: new Date(),
      type: logType,
      message,
    });
  }

  private updateStats(stats: ClientStats): void {
    this.messagesSentEl.textContent = stats.messagesSent.toString();
    this.messagesReceivedEl.textContent = stats.messagesReceived.toString();

    if (stats.lastPing) {
      this.lastPingEl.textContent = stats.lastPing.toLocaleTimeString('en-US');
    } else {
      this.lastPingEl.textContent = '-';
    }
  }

  private startConnectionTimer(): void {
    if (this.connectionTimeInterval) {
      clearInterval(this.connectionTimeInterval);
    }

    this.connectionTimeInterval = window.setInterval(() => {
      const stats = this.client.getStats();
      if (stats.connectionTime) {
        const duration = Date.now() - stats.connectionTime.getTime();
        const seconds = Math.floor(duration / 1000);
        const minutes = Math.floor(seconds / 60);
        const hours = Math.floor(minutes / 60);

        const displayHours = hours.toString().padStart(2, '0');
        const displayMinutes = (minutes % 60).toString().padStart(2, '0');
        const displaySeconds = (seconds % 60).toString().padStart(2, '0');

        this.connectionTimeEl.textContent = `${displayHours}:${displayMinutes}:${displaySeconds}`;
      }
    }, 1000);
  }

  private saveSettings(): void {
    const settings = {
      clientId: this.clientIdInput.value,
      clientSecret: this.clientSecretInput.value,
      preset:
        (document.querySelector('input[name="credentialPreset"]:checked') as HTMLInputElement)
          ?.value || 'demo1',
      showPlaybackStats: this.showPlaybackStatsCheckbox.checked,
      muteVideos: this.muteVideosCheckbox.checked,
    };
    localStorage.setItem('winsignage-settings', JSON.stringify(settings));
  }

  private loadSettings(): void {
    const savedSettings = localStorage.getItem('winsignage-settings');
    if (savedSettings) {
      try {
        const settings = JSON.parse(savedSettings);

        // Load saved preset
        const preset = settings.preset || 'demo1';
        const radioToCheck = document.querySelector(
          `input[name="credentialPreset"][value="${preset}"]`
        ) as HTMLInputElement;
        if (radioToCheck) {
          radioToCheck.checked = true;
          this.handlePresetChange(preset);
        }

        // If custom preset, restore saved values
        if (preset === 'custom' && settings.clientId && settings.clientSecret) {
          this.clientIdInput.value = settings.clientId;
          this.clientSecretInput.value = settings.clientSecret;
        }

        // Load playback stats visibility
        if (settings.showPlaybackStats !== undefined) {
          this.showPlaybackStatsCheckbox.checked = settings.showPlaybackStats;
          this.applyPlaybackStatsVisibility();
        } else {
          // Default to false if not set
          this.showPlaybackStatsCheckbox.checked = false;
          this.applyPlaybackStatsVisibility();
        }

        // Load mute videos setting
        if (settings.muteVideos !== undefined) {
          this.muteVideosCheckbox.checked = settings.muteVideos;
        } else {
          // Default to true (muted) if not set
          this.muteVideosCheckbox.checked = true;
        }
      } catch (error) {
        console.error('Failed to load settings:', error);
      }
    } else {
      // Use default preset (demo1)
      const demo1Radio = document.querySelector(
        'input[name="credentialPreset"][value="demo1"]'
      ) as HTMLInputElement;
      if (demo1Radio) {
        demo1Radio.checked = true;
        this.handlePresetChange('demo1');
      }
      // Default mute videos to true
      this.muteVideosCheckbox.checked = true;
      // Default playback stats to false
      this.showPlaybackStatsCheckbox.checked = false;
      this.applyPlaybackStatsVisibility();
    }
  }

  private setupSettingsButtonAutoHide(): void {
    // Start initial timer to hide button after 5 seconds
    this.startSettingsButtonTimer();

    // Show button on mouse move or touch
    document.addEventListener('mousemove', () => this.showSettingsButton());
    document.addEventListener('touchstart', () => this.showSettingsButton());
    document.addEventListener('click', () => this.showSettingsButton());
  }

  private startSettingsButtonTimer(): void {
    // Clear existing timer
    if (this.settingsButtonTimer) {
      clearTimeout(this.settingsButtonTimer);
    }

    // Set new timer to hide button after 5 seconds
    this.settingsButtonTimer = window.setTimeout(() => {
      this.hideSettingsButton();
    }, 5000);
  }

  private showSettingsButton(): void {
    if (this.settingsToggleBtn) {
      this.settingsToggleBtn.classList.remove('hidden');
      this.startSettingsButtonTimer();
    }
  }

  private hideSettingsButton(): void {
    if (this.settingsToggleBtn) {
      this.settingsToggleBtn.classList.add('hidden');
    }
  }

  private setupPlaybackControlsAutoHide(): void {
    // Show controls on mouse move or touch
    document.addEventListener('mousemove', () => this.showPlaybackControls());
    document.addEventListener('touchstart', () => this.showPlaybackControls());
    document.addEventListener('click', () => this.showPlaybackControls());
  }

  private startPlaybackControlsTimer(): void {
    // Clear existing timer
    if (this.playbackControlsTimer) {
      clearTimeout(this.playbackControlsTimer);
    }

    // Set new timer to hide controls after 5 seconds
    this.playbackControlsTimer = window.setTimeout(() => {
      this.hidePlaybackControls();
    }, 5000);
  }

  private showPlaybackControls(): void {
    if (this.playbackControls && this.playbackControls.style.display !== 'none') {
      this.playbackControls.classList.remove('hidden');
      this.startPlaybackControlsTimer();
    }
  }

  private hidePlaybackControls(): void {
    if (this.playbackControls && this.playbackControls.style.display !== 'none') {
      this.playbackControls.classList.add('hidden');
    }
  }

  private setupFullscreenButtonAutoHide(): void {
    // Start initial timer to hide button after 5 seconds
    this.startFullscreenButtonTimer();

    // Show button on mouse move or touch
    document.addEventListener('mousemove', () => this.showFullscreenButton());
    document.addEventListener('touchstart', () => this.showFullscreenButton());
    document.addEventListener('click', () => this.showFullscreenButton());
  }

  private startFullscreenButtonTimer(): void {
    // Clear existing timer
    if (this.fullscreenButtonTimer) {
      clearTimeout(this.fullscreenButtonTimer);
    }

    // Set new timer to hide button after 5 seconds
    this.fullscreenButtonTimer = window.setTimeout(() => {
      this.hideFullscreenButton();
    }, 5000);
  }

  private showFullscreenButton(): void {
    if (this.fullscreenToggleBtn) {
      this.fullscreenToggleBtn.classList.remove('hidden');
      this.startFullscreenButtonTimer();
    }
  }

  private hideFullscreenButton(): void {
    if (this.fullscreenToggleBtn) {
      this.fullscreenToggleBtn.classList.add('hidden');
    }
  }

  private async toggleFullscreen(): Promise<void> {
    const elem = document.documentElement as any;

    // Check if already in fullscreen
    const isFullscreen =
      document.fullscreenElement ||
      (document as any).webkitFullscreenElement ||
      (document as any).mozFullScreenElement ||
      (document as any).msFullscreenElement;

    if (!isFullscreen) {
      // Enter fullscreen - try different methods for browser compatibility
      try {
        if (elem.requestFullscreen) {
          await elem.requestFullscreen();
        } else if (elem.webkitRequestFullscreen) {
          // iOS Safari
          await elem.webkitRequestFullscreen();
        } else if (elem.webkitEnterFullscreen) {
          // Older iOS
          await elem.webkitEnterFullscreen();
        } else if (elem.mozRequestFullScreen) {
          // Firefox
          await elem.mozRequestFullScreen();
        } else if (elem.msRequestFullscreen) {
          // IE/Edge
          await elem.msRequestFullscreen();
        }
      } catch (err) {
        console.error('Error attempting to enable fullscreen:', err);
      }
    } else {
      // Exit fullscreen
      try {
        if (document.exitFullscreen) {
          await document.exitFullscreen();
        } else if ((document as any).webkitExitFullscreen) {
          await (document as any).webkitExitFullscreen();
        } else if ((document as any).mozCancelFullScreen) {
          await (document as any).mozCancelFullScreen();
        } else if ((document as any).msExitFullscreen) {
          await (document as any).msExitFullscreen();
        }
      } catch (err) {
        console.error('Error attempting to exit fullscreen:', err);
      }
    }
  }

  private setupFullscreenWakeLock(): void {
    // Listen for fullscreen changes - support multiple browsers
    const fullscreenEvents = [
      'fullscreenchange',
      'webkitfullscreenchange',
      'mozfullscreenchange',
      'msfullscreenchange',
    ];

    fullscreenEvents.forEach((eventName) => {
      document.addEventListener(eventName, () => {
        const isFullscreen =
          document.fullscreenElement ||
          (document as any).webkitFullscreenElement ||
          (document as any).mozFullScreenElement ||
          (document as any).msFullscreenElement;

        if (isFullscreen) {
          this.requestWakeLock();
        } else {
          this.releaseWakeLock();
        }
      });
    });
  }

  private async requestWakeLock(): Promise<void> {
    try {
      if ('wakeLock' in navigator) {
        this.wakeLock = await navigator.wakeLock.request('screen');
        console.log('🔒 Wake Lock aktif - Ekran açık kalacak');

        this.wakeLock.addEventListener('release', () => {
          console.log('🔓 Wake Lock serbest bırakıldı');
        });
      } else {
        console.warn('⚠️ Wake Lock API bu tarayıcıda desteklenmiyor');
      }
    } catch (err) {
      console.error('❌ Wake Lock hatası:', err);
    }
  }

  private async releaseWakeLock(): Promise<void> {
    if (this.wakeLock !== null) {
      try {
        await this.wakeLock.release();
        this.wakeLock = null;
        console.log('🔓 Wake Lock manuel olarak serbest bırakıldı');
      } catch (err) {
        console.error('❌ Wake Lock serbest bırakma hatası:', err);
      }
    }
  }

  private loadPlaybackCounts(): void {
    try {
      const saved = localStorage.getItem(this.PLAYBACK_COUNTS_KEY);
      if (saved) {
        const data = JSON.parse(saved);
        this.assetPlaybackCounts = new Map(Object.entries(data));
        console.log('📊 Playback counts loaded:', this.assetPlaybackCounts.size, 'assets');
      }
    } catch (error) {
      console.error('❌ Failed to load playback counts:', error);
      this.assetPlaybackCounts = new Map();
    }
  }

  private savePlaybackCounts(): void {
    try {
      const data = Object.fromEntries(this.assetPlaybackCounts);
      localStorage.setItem(this.PLAYBACK_COUNTS_KEY, JSON.stringify(data));
      this.updatePlaybackStatsUI();
    } catch (error) {
      console.error('❌ Failed to save playback counts:', error);
    }
  }

  private incrementAssetPlayback(assetId: string, playlistId?: string): void {
    const key = playlistId ? `${assetId}|${playlistId}` : assetId;
    const currentCount = this.assetPlaybackCounts.get(key) || 0;
    this.assetPlaybackCounts.set(key, currentCount + 1);
    this.savePlaybackCounts();
    console.log(`📊 Asset ${assetId} (Playlist: ${playlistId || 'None'}) played ${currentCount + 1} times`);
  }

  private getPlaybackStatsForServer(): any {
    const stats: any = {};
    this.assetPlaybackCounts.forEach((count, key) => {
      stats[key] = count;
    });

    // Reset counts after sending to server
    this.assetPlaybackCounts.clear();
    this.savePlaybackCounts();
    console.log('📊 Playback stats sent and reset to 0');

    return stats;
  }

  private updatePlaybackStatsUI(): void {
    if (!this.playbackStatsContent) return;

    if (this.assetPlaybackCounts.size === 0) {
      this.playbackStatsContent.innerHTML = '<div class="stats-empty">No playback data yet</div>';
      return;
    }

    // Sort by count (descending) and create HTML
    const sortedStats = Array.from(this.assetPlaybackCounts.entries()).sort((a, b) => b[1] - a[1]);

    const html = sortedStats
      .map(
        ([assetId, count]) => `
        <div class="stat-item">
          <div class="stat-label" title="${assetId}">${assetId}</div>
          <div class="stat-value">${count}</div>
        </div>
      `
      )
      .join('');

    this.playbackStatsContent.innerHTML = html;
  }

  private togglePlaybackStats(): void {
    this.applyPlaybackStatsVisibility();
    this.saveSettings();
  }

  private handleMuteVideosChange(): void {
    // If video is currently playing, update its muted state
    if (this.videoPlayer.classList.contains('active')) {
      this.videoPlayer.muted = this.muteVideosCheckbox.checked;
      this.log(`🔊 Video ${this.muteVideosCheckbox.checked ? 'muted' : 'unmuted'}`, LogType.Info);
    }
    this.saveSettings();
  }

  private applyPlaybackStatsVisibility(): void {
    if (this.showPlaybackStatsCheckbox.checked) {
      this.playbackStatsOverlayElement.classList.remove('hidden');
      this.playbackStatsOverlayElement.classList.add('visible');
    } else {
      this.playbackStatsOverlayElement.classList.remove('visible');
      this.playbackStatsOverlayElement.classList.add('hidden');
    }
  }

  private cleanupOldPlaybackCounts(currentAssets: any[]): void {
    // Get all current asset IDs
    const currentAssetIds = new Set<string>();
    currentAssets.forEach((asset) => {
      const assetId = asset.id || asset.assetId || asset.asset_id;
      if (assetId) {
        currentAssetIds.add(assetId);
      }
    });

    // Remove counts for assets not in current playlist
    const idsToRemove: string[] = [];
    this.assetPlaybackCounts.forEach((_count, assetId) => {
      if (!currentAssetIds.has(assetId)) {
        idsToRemove.push(assetId);
      }
    });

    idsToRemove.forEach((id) => {
      this.assetPlaybackCounts.delete(id);
    });

    if (idsToRemove.length > 0) {
      this.savePlaybackCounts();
      console.log(`🧹 Cleaned up ${idsToRemove.length} old asset(s) from playback counts`);
    }
  }
}

// Initialize app when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  new App();
  console.log('?? WinSignage WebSocket Client initialized');
});
